'use client';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { faCircleQuestion, faLockKeyhole, faPlus } from '@fortawesome/pro-light-svg-icons';
import { AnimatePresence, motion } from 'framer-motion';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.component';
import { cpfValidator } from '@/utils/validators';
import { useToast } from '@/components/ui/use-toast';
import { Icon } from '@/components/ui/icon.component';
import { cellphoneValidator } from '@/utils/validators/cellphone.validator';
import { datePastValidator } from '@/utils/validators/date.validator';
import { completeNameValidator } from '@/utils/validators/complete-name.validator';
import { checkIfCpfExists, createAccount } from '@/services/auth/registration.service';
import { generateRegistrationCookies, getRegistrationCookies } from '@/cookies/registration.cookies';
import { StepKeys } from '@/app/(main)/_components/sign-in-modal/_types/header-auth-modal-step-keys.enum';
import SelectCities from '@/components/ui/select-cities.component';
import { cityAndStateValidator } from '@/utils/validators/city-and-state.validator';
import { onlyTextValidator } from '@/utils/validators/only-text-validator';
import { useProgressiveModal } from '@/components/ui/progressive-modal/progressive-modal.hook';
import { getLogger } from '@/logger/logger';

const createAccountFormSchema = z.object({
  legalName: z
    .custom<string>(completeNameValidator, {
      message: 'Digite seu nome completo',
    })
    .refine(onlyTextValidator, {
      message: 'Use apenas letras do alfabeto',
    }),
  socialName: z.custom<string>((data) => completeNameValidator(data, true), {
    message: 'Digite seu nome social completo',
  }),
  birthdate: z.custom<string>(datePastValidator, {
    message: 'Digite uma data de nascimento válida',
  }),
  documentNumber: z.custom<string>(cpfValidator, {
    message: 'Digite um CPF válido',
  }),
  city: z.custom<string>(cityAndStateValidator, {
    message: 'Digite sua cidade',
  }),
  cellphone: z.custom<string>(cellphoneValidator, {
    message: 'Digite um telefone válido',
  }),
  email: z
    .string()
    .nonempty('Digite um e-mail válido')
    .email({
      message: 'Digite um endereço de e-mail válido',
    }),
  password: z
    .string()
    .nonempty('Digite uma senha')
    .regex(/^(?=.*[0-9])(?=.*[a-zA-Z])(?=\S+$).{6,20}$/gm, {
      message: 'A senha não atende aos requisitos mínimos',
    })
    .max(20),
});

export type CreateAccountForm = z.infer<typeof createAccountFormSchema>;

const defaultValues: Partial<CreateAccountForm> = {
  legalName: '',
  socialName: '',
  birthdate: '',
  documentNumber: '',
  city: '',
  cellphone: '',
  email: '',
  password: '',
};

type City = {
  city: string;
  uf?: string;
};

export default function RegisterForm() {
  const { handleJump: jump } = useProgressiveModal<StepKeys>();
  const { toast } = useToast();

  const [showSocialName, setShowSocialName] = useState(false);
  const [hideSocialNameOption, setHideSocialNameOption] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [cpfExists, setCpfExists] = useState(false);
  const [isComingBack, setIsComingBack] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(false);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  const form = useForm<CreateAccountForm>({
    resolver: zodResolver(createAccountFormSchema),
    defaultValues,
    mode: 'all',
    delayError: 100,
  });

  const handleDisplaySocialNameTip = () => {
    toast.info(
      'O nome social é a forma como você se identifica e é chamado(a). Ele pode ser diferente do nome que consta no seu documento.',
      {
        duration: 6000,
      },
    );
  };

  useEffect(() => {
    if (selectedCity) {
      form.setValue('city', `${selectedCity.city} - ${selectedCity.uf}`);
      form.trigger('city');
    }
  }, [form, selectedCity]);

  useEffect(() => {
    if (!isComingBack) {
      setInitialLoad(true);
    }
  }, [isComingBack]);

  useEffect(() => {
    async function fetchRegistrationData() {
      const registrationCookies = await getRegistrationCookies();
      if (!registrationCookies) {
        return;
      }
      const { formData, hideSocialName } = registrationCookies;
      const hasSocialName = !!formData.socialName;
      if (hasSocialName) {
        setShowSocialName(hasSocialName);
      }
      if (hideSocialName) {
        setHideSocialNameOption(true);
      }
      const hasCpf = !!formData.documentNumber && cpfValidator(formData.documentNumber);

      for (const key in formData) {
        if (!formData[key as keyof CreateAccountForm] || formData[key as keyof CreateAccountForm] === '') {
          continue;
        }
        form.setValue(key as keyof CreateAccountForm, formData[key as keyof CreateAccountForm]);
        form.trigger(key as keyof CreateAccountForm);
      }

      const city = formData.city.split(' - ');

      if (city.length === 2) {
        setSelectedCity({ city: city[0], uf: city[1] });
      }

      setIsComingBack(hasCpf);
    }
    if (initialLoad) {
      fetchRegistrationData();
    }
  }, [form, initialLoad]);

  const checkIfCpfAlreadyRegistered = async (cpf: string) => {
    setIsVerified(true);

    const cpfIsValid = cpfValidator(cpf);

    if (cpfIsValid) {
      const cpfExists = await checkIfCpfExists(cpf);
      setCpfExists(cpfExists?.exists);
    }
  };

  const createOrUpdateRegistrationCookies = async () => {
    const registrationCookies = await getRegistrationCookies();
    const partialFormData = form.getValues();

    generateRegistrationCookies({
      ...(registrationCookies || { token: '', message: '', maskedEmail: '' }),
      formData: partialFormData,
    });
  };

  const handleReadTermsOfUse = async () => {
    await createOrUpdateRegistrationCookies();
    jump({ key: StepKeys.TERMS_OF_USE });
  };

  const handleReadPrivacyPolicy = async () => {
    await createOrUpdateRegistrationCookies();
    jump({ key: StepKeys.PRIVACY_POLICY });
  };

  useEffect(() => {
    if (isComingBack) {
      setCpfExists(false);
      setIsVerified(false);
      return;
    }
    const hasCpfError = !!form.formState.errors.documentNumber;
    const hasValidValue = !!(form.getValues('documentNumber') && form.getValues('documentNumber').length === 14);

    const shouldFind = !hasCpfError && hasValidValue && !isVerified;

    if (!hasValidValue) {
      setCpfExists(false);
      setIsVerified(false);
    }

    if (!shouldFind) {
      return;
    }

    checkIfCpfAlreadyRegistered(form.getValues('documentNumber'));
  }, [cpfExists, form, isVerified, form.formState, isComingBack]);

  function handleCpfPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    checkIfCpfAlreadyRegistered(e.clipboardData.getData('text'));
  }

  async function onSubmit(data: CreateAccountForm) {
    setIsLoading(true);

    try {
      const response = await createAccount(data);
      const registrationCookies = await getRegistrationCookies();
      generateRegistrationCookies({
        maskedEmail: response.maskedEmail,
        message: response.message,
        token: response.token,
        formData: data,
        lastPinRequest: registrationCookies?.lastPinRequest,
      });
      jump({ key: StepKeys.PIN_CONFIRMATION });
    } catch (error) {
      const logger = getLogger('SignInModal - RegisterForm');
      logger.error('Error while creating account', { fields: form.getValues(), error });
      toast.error('Erro interno do sistema. Por favor, entre em contato com o suporte.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} aria-label="form" className="space-y-6 pb-5">
        <FormField
          control={form.control}
          name="documentNumber"
          render={({ field, fieldState }) => {
            return (
              <>
                <FormItem>
                  <FormLabel className="font-semibold text-gray-900">CPF</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="000.000.000-00"
                      mask="999.999.999-99"
                      type="tel"
                      shouldClearValue={!isComingBack}
                      disabled={isComingBack}
                      {...field}
                      className={`${cpfExists ? 'border-danger-600' : ''} !text-clip`}
                      onPaste={handleCpfPaste}
                      data-testid="register--input-cpf"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
                {!fieldState.invalid && cpfExists && (
                  <>
                    <span className="text-danger-600 text-sm">CPF já cadastrado. Tente acessar sua conta.</span>
                  </>
                )}
              </>
            );
          }}
        />
        <FormField
          control={form.control}
          name="legalName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold text-gray-900">Nome (como está no documento)</FormLabel>
              <FormControl>
                <Input
                  placeholder="Digite o nome completo"
                  {...field}
                  value={field.value as string}
                  shouldClearValue
                  className=" !text-clip"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!showSocialName && !hideSocialNameOption && (
          <div className="text-green-500 text-base flex justify-between hover:underline cursor-pointer">
            <div
              className="flex gap-2"
              onClick={() => {
                setShowSocialName(true);
              }}
            >
              <div className="w-5 h-5 flex flex-center justify-center self-center">
                <Icon icon={faPlus} className="text-base self-center" />
              </div>
              <span className="font-semibold">Incluir nome social (opcional)</span>
            </div>
            <div
              className="w-5 h-5 flex flex-center justify-center self-center cursor-pointer"
              onClick={handleDisplaySocialNameTip}
            >
              <Icon icon={faCircleQuestion} className="text-base self-center" />
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="socialName"
          render={({ field }) => (
            <AnimatePresence initial={true}>
              {showSocialName && (
                <motion.div
                  key="socialName"
                  initial={{ opacity: 0, height: 0, y: -80 }}
                  animate={{ opacity: 1, height: 'auto', y: 0 }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    x: { type: 'keyframes', duration: 0.2 },
                  }}
                >
                  <div>
                    <FormItem>
                      <FormLabel className="font-semibold text-gray-900">Nome social</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Digite o nome social (opcional)"
                          shouldClearValue
                          {...field}
                          className=" !text-clip"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        />

        <FormField
          control={form.control}
          name="birthdate"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold text-gray-900">Data de nascimento</FormLabel>
              <FormControl>
                <Input placeholder="DD/MM/AAAA" type="tel" mask="99/99/9999" shouldClearValue {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold text-gray-900">Cidade</FormLabel>
              <FormControl>
                <SelectCities
                  {...field}
                  selectedCity={selectedCity}
                  onChange={setSelectedCity}
                  hasError={!!form.formState.errors.city}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="cellphone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold text-gray-900">Celular</FormLabel>
              <FormControl>
                <Input
                  placeholder="DDD + Número"
                  type="tel"
                  shouldClearValue
                  mask={field.value.length > 14 ? '(99) 9 9999-9999' : '(99) 9999-99999'}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold text-gray-900">E-mail</FormLabel>
              <FormControl>
                <Input
                  placeholder="email@email.com"
                  shouldClearValue
                  {...field}
                  className=" !text-clip"
                  autoComplete="off"
                />
              </FormControl>

              <FormMessage />
              <FormDescription className="font-light text-sm text-gray-900">
                Este e-mail será usado para validar sua conta
              </FormDescription>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-semibold text-gray-900">Senha</FormLabel>
              <FormControl>
                <Input
                  startIcon={faLockKeyhole}
                  placeholder="Escolha uma senha segura"
                  type="password"
                  {...field}
                  className=" !text-clip"
                  autoComplete="off"
                  data-testid="register--input-password"
                />
              </FormControl>

              <FormMessage />
              <FormDescription className="font-light text-sm text-gray-900">
                A senha deve conter no mínimo 6 caracteres. Use pelo menos uma letra e um número.
              </FormDescription>
            </FormItem>
          )}
        />
        <div className="text-base text-gray-700 ">
          Ao clicar em Criar conta, você concorda com os
          {' '}
          <span
            className="text-green-500 cursor-pointer font-bold"
            data-testid="span-terms--register-form"
            onClick={handleReadTermsOfUse}
          >
            Termos de Uso
          </span>
          {' '}
          e
          {' '}
          <span
            className="text-green-500 cursor-pointer font-bold"
            data-testid="span-policy--register-form"
            onClick={handleReadPrivacyPolicy}
          >
            Política de Privacidade
          </span>
          .
        </div>
        <Button
          type="submit"
          variant="default"
          color="primary"
          className="w-full h-12"
          data-testid="button-submit--register-form"
          disabled={!form.formState.isValid}
        >
          {isLoading && (
            <div className="flex flex-row gap-2">
              <div className="h-2 w-2 bg-white rounded-full animate-bounce-lg [animation-delay:-0.3s]"></div>
              <div className="h-2 w-2 bg-white rounded-full animate-bounce-lg [animation-delay:-0.15s]"></div>
              <div className="h-2 w-2 bg-white rounded-full animate-bounce-lg"></div>
            </div>
          )}
          {!isLoading && 'Criar conta'}
        </Button>
      </form>
    </Form>
  );
}
